/**
 * planStagingObjects — the entry point a caller persists.
 *
 * STRIP ORDER IS LOAD-BEARING. Strip the previous run's props FIRST, then plan
 * against the stripped room. Planning first and stripping after is the bug
 * dorm shipped and had to fix: the placers treat everything already in the
 * room as an obstacle, so last run's tray blocks this run's tray, nothing is
 * emitted, and the strip then deletes the old one. The room shows decor in the
 * preview and loses it on save. Do not reorder.
 *
 * Re-staging is therefore idempotent by RE-PLANNING, not by diffing: the props
 * are regenerated against the room's current furniture rather than preserved
 * where they were. That is what makes "run it again after the layout changed"
 * a single call.
 */

const { isStagingDecor, STAGING_TOOL } = require('./ownership');
const { resolveStagingStyle } = require('./stagingStyles');
const { planBathroomStaging } = require('./plan');
const { planKitchenStaging } = require('./plan/kitchen');
const { planBedroomStagingDetailed } = require('./plan/bedroom');
const { stagingKitsFor } = require('./stagingKind');

// One planner per kit (`stagingKitsFor`, `./stagingKind.js`). A room no kit
// claims plans nothing: no gate offers it, and the tool refuses it.
const PLANNERS = {
  kitchen: (room, style, opts) => ({
    entries: planKitchenStaging(room, style, opts),
  }),
  bedroom: (room, style, opts) => planBedroomStagingDetailed(room, style, opts),
  bathroom: (room, style, opts) => ({
    entries: planBathroomStaging(room, style, opts),
  }),
};

/**
 * @param {Object} room  a hydrated room (real opening geometry, not slim copies)
 * @param {Object|string} style  a resolved pack, or a style id
 * @param {Object} [opts]
 * @param {boolean} [opts.persistIds]  mint fresh ids (save) vs keep planner-local (preview)
 * @param {() => string} [opts.freshId]
 * @param {string} [opts.generatedAt]  ISO stamp for provenance
 * @param {boolean} [opts.addNightstands]  false: never bring a staged
 *   nightstand (the bedroom kit then dresses only scanned ones)
 * @returns {{ decor: Object[], objects: Object[], stagedHosts: Object[] }}
 *   `decor` is what a live preview renders; `objects` is the room's full
 *   replacement array; `stagedHosts` is the room's furniture to draw as the
 *   staged built-in piece (`./stagedHosts.js`) — the room's full replacement
 *   for that field, empty when nothing is swapped.
 */
/**
 * Re-mint every entry's id, carrying its `parents` reference with it.
 *
 * The props standing ON the tray are parented to the tray by its
 * planner-local id (`staging-staging-tray-4`, minted in `plan/index.js`).
 * Re-minting ids without rewriting those references left the candle and the
 * jar pointing at an id that no longer existed -- 32 of 119 links across the
 * corpus. `parents` is what makes `moveGroup` drag them when the tray moves
 * and `moveUnit` carry them through a collision nudge, and BOTH fail
 * silently on a dangling id (neither throws, neither logs, the prop is just
 * never admitted to the group). So the tray's own props were the ones it
 * could not carry.
 *
 * Two passes, because an entry's parent may be re-minted after it. A parent
 * the map does not know is left alone: that is a real scanned `countertop`,
 * whose id this never touches.
 */
function repointToFreshIds(decor, freshId) {
  const remap = new Map();
  const reIded = decor.map((entry) => {
    const id = freshId();
    if (entry.id) remap.set(entry.id, id);
    return { ...entry, id };
  });
  return reIded.map((entry) =>
    Array.isArray(entry.parents) && entry.parents.some((p) => remap.has(p?.id))
      ? {
          ...entry,
          parents: entry.parents.map((p) =>
            remap.has(p?.id) ? { ...p, id: remap.get(p.id) } : p
          ),
        }
      : entry
  );
}

function planStagingObjects(room, style, opts = {}) {
  const resolved =
    typeof style === 'string' || style == null
      ? resolveStagingStyle(style)
      : style;

  const kept = (room?.objects || [])
    .filter(Boolean)
    .filter((o) => !isStagingDecor(o));
  const strippedRoom = { ...room, objects: kept };

  const generatedAt = opts.generatedAt || new Date().toISOString();
  const planOpts = {
    idPrefix: opts.idPrefix,
    generatedAt,
    onDrop: opts.onDrop,
    addNightstands: opts.addNightstands,
  };
  let decor = [];
  let stagedHosts = [];
  const prefix = opts.idPrefix || 'staging';
  stagingKitsFor(strippedRoom).forEach((kit, i) => {
    // A later kit plans around an earlier one's props, as it would around
    // the room's own furniture. Planner-local ids get a per-kit prefix so two
    // kits in one room cannot collide; the first kit keeps the bare one (the
    // golden output pins it), except the bedroom's, which is always `-bed`.
    const around = { ...strippedRoom, objects: [...kept, ...decor] };
    const idPrefix =
      kit === 'bedroom'
        ? `${prefix}-bed`
        : i === 0
          ? opts.idPrefix
          : `${prefix}-${kit}`;
    const planned = PLANNERS[kit](around, resolved, { ...planOpts, idPrefix });
    decor = [...decor, ...planned.entries];
    stagedHosts = [...stagedHosts, ...(planned.stagedHosts || [])];
  });

  if (opts.persistIds && typeof opts.freshId === 'function') {
    decor = repointToFreshIds(decor, opts.freshId);
  }

  return { decor, objects: [...kept, ...decor], stagedHosts };
}

module.exports = { planStagingObjects, STAGING_TOOL };
